import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import type {
  CategoryReportDto,
  GroupMembersReportDto,
  GroupSpendingReportDto,
  MemberExpenseReportDto,
  MonthlyReportDto,
  SpendingTrendsDto,
  YearlyReportDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

// Fetch group spending report
export function useGroupSpendingReportQuery(
  groupId: string,
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.spending(groupId, { startDate, endDate }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(
        `/groups/${groupId}/reports/spending${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
      return (response as unknown as { data: GroupSpendingReportDto }).data;
    },
    enabled: enabled && !!groupId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch monthly reports
export function useGroupMonthlyReportQuery(
  groupId: string,
  startMonth?: string,
  endMonth?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.monthly(groupId, {
      startMonth,
      endMonth,
    }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startMonth) params.append('startMonth', startMonth);
      if (endMonth) params.append('endMonth', endMonth);

      const response = await api.get(
        `/groups/${groupId}/reports/monthly${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
      return (response as unknown as { data: MonthlyReportDto[] }).data;
    },
    enabled: enabled && !!groupId && !!startMonth && !!endMonth,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch category breakdown report
export function useGroupCategoryReportQuery(
  groupId: string,
  startMonth?: string,
  endMonth?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.category(groupId, {
      startMonth,
      endMonth,
    }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startMonth) params.append('startMonth', startMonth);
      if (endMonth) params.append('endMonth', endMonth);

      const response = await api.get(
        `/groups/${groupId}/reports/category${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
      return (response as unknown as { data: CategoryReportDto[] }).data;
    },
    enabled: enabled && !!groupId && !!startMonth && !!endMonth,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch yearly report
export function useGroupYearlyReportQuery(
  groupId: string,
  year: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.yearly(groupId, year),
    queryFn: async () => {
      const response = await api.get(
        `/groups/${groupId}/reports/yearly?year=${year}`
      );
      return (response as unknown as { data: YearlyReportDto }).data;
    },
    enabled: enabled && !!groupId && !!year,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch spending trends
export function useGroupSpendingTrendsQuery(
  groupId: string,
  startMonth?: string,
  endMonth?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.trends(groupId, { startMonth, endMonth }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startMonth) params.append('startMonth', startMonth);
      if (endMonth) params.append('endMonth', endMonth);

      const response = await api.get(
        `/groups/${groupId}/reports/trends${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
      return (response as unknown as { data: SpendingTrendsDto[] }).data;
    },
    enabled: enabled && !!groupId && !!startMonth && !!endMonth,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch group members report (all members overview)
export function useGroupMembersReportQuery(
  groupId: string,
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.members(groupId, { startDate, endDate }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(
        `/groups/${groupId}/reports/members${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
      return (response as unknown as { data: GroupMembersReportDto }).data;
    },
    enabled: enabled && !!groupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch individual member expense report (drill-down view)
export function useMemberExpenseReportQuery(
  groupId: string,
  memberId: string,
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupReports.member(groupId, memberId, {
      startDate,
      endDate,
    }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(
        `/groups/${groupId}/reports/members/${memberId}${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
      return (response as unknown as { data: MemberExpenseReportDto }).data;
    },
    enabled: enabled && !!groupId && !!memberId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
